import { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useAddQrTemplateMutation, useUpdateQrTemplateMutation } from "../../../api/managementApi";
import { SoftButton, cls, useDialogPaperSx } from "../../hatim/soft/SoftKit";
import GoalWizardDrawer from "../../goals/GoalWizard";
import GoalListRow from "../../goals/GoalListRow";
import { goalToInitial, hasDuplicateGoal, toQrItem } from "../../goals/goalRules";

/** Sunucudaki sınırlar (qrGoals.js QR_NAME_MAX, goalItems.js MAX_GOAL_ITEMS). */
export const QR_NAME_MAX = 40;
export const QR_MAX_GOALS = 20;

/** Kaydedilmiş QR kalemi düzenlenebilir listeye: boş alanlar yazılmaz. */
function cloneItem(item) {
  const out = { ...item };
  if (Array.isArray(item.weekDays)) out.weekDays = [...item.weekDays];
  return out;
}

/**
 * Listeye kalem ekler ya da düzenler; aynı ad + biçim başka satırda varsa
 * ya da sınır aşılıyorsa hata anahtarı döner.
 */
export function upsertQrItem(items, item, editIndex) {
  const next = editIndex === null ? [...items, item] : items.map((g, i) => (i === editIndex ? item : g));
  if (hasDuplicateGoal(next)) return { error: "manage_template_duplicate" };
  if (next.length > QR_MAX_GOALS) return { error: "manage_template_too_many" };
  return { items: next };
}

/**
 * QR katılım şablonu: katılan kişiye eklenecek hedefler. Her hedef takip
 * ayarlı sihirbazla kurulur (eser, biçim, birim, plan, günler); sihirbaz
 * kaydetmez, kalemi listeye koyar. `template` verilirse düzenleme.
 */
export default function AddTemplateModal({ open, groupId, template, onClose, onCreated }) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  // null: kapalı; { index: null } yeni kalem; { index: n } n. kalemi düzenle
  const [wizard, setWizard] = useState(null);
  const [wizardError, setWizardError] = useState("");
  const [addTemplate, { isLoading: adding }] = useAddQrTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateQrTemplateMutation();
  const isEdit = !!template;
  const saving = adding || updating;

  useEffect(() => {
    if (!open) return;
    setItems((template?.goalTemplates || []).map(cloneItem));
    setName(template?.groupName || "");
    setError("");
    setWizard(null);
  }, [open, template]);

  const openWizard = (index) => {
    setWizardError("");
    setWizard({ index });
  };

  const editingItem = wizard && wizard.index !== null ? items[wizard.index] : null;

  const handleWizardSubmit = (value) => {
    const res = upsertQrItem(items, toQrItem(value), wizard?.index ?? null);
    if (res.error) {
      setWizardError(t(res.error));
      return;
    }
    setItems(res.items);
    setError("");
    setWizard(null);
  };

  const handleSubmit = async () => {
    if (!items.length || saving) return;
    const payload = { groupId, goalTemplates: items, groupName: name.trim() || undefined };
    try {
      if (isEdit) await updateTemplate({ ...payload, templateId: template.id }).unwrap();
      else await addTemplate(payload).unwrap();
      onCreated && onCreated();
      onClose && onClose();
    } catch (err) {
      setError(err?.message || err?.data?.message || t("manage_template_create_error"));
    }
  };

  return (
    <>
      <Dialog open={!!open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: paperSx }} disableEnforceFocus>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t(isEdit ? "manage_template_edit_title" : "manage_template_modal_title")}
        </DialogTitle>
        <DialogContent sx={{ px: 0 }}>
          <div className="px-6 pt-1 pb-3">
            <TextField
              fullWidth
              size="small"
              label={t("manage_template_name")}
              placeholder={t("manage_template_name_hint")}
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, QR_NAME_MAX))}
              className={cls.input}
            />
          </div>
          {items.length === 0 ? (
            <p className={`px-6 py-2 text-sm ${cls.muted}`}>{t("manage_template_no_goals")}</p>
          ) : (
            <ul>
              {items.map((item, i) => (
                <GoalListRow
                  key={`${item.label}::${item.activity || "okuma"}`}
                  goal={{ ...item, goalValue: item.defaultValue }}
                  onEdit={() => openWizard(i)}
                  onDelete={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
                />
              ))}
            </ul>
          )}
          <div className="px-6 pt-3">
            <SoftButton
              tone="soft"
              onClick={() => openWizard(null)}
              disabled={items.length >= QR_MAX_GOALS}
            >
              {t("manage_template_goal_add")}
            </SoftButton>
          </div>
          {error ? (
            <p role="alert" className={`px-6 pt-3 text-sm ${cls.danger}`}>
              {error}
            </p>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <SoftButton tone="cancel" onClick={onClose} disabled={saving}>
            {t("manage_template_cancel")}
          </SoftButton>
          <SoftButton tone="success" onClick={handleSubmit} disabled={!items.length || saving}>
            {t(isEdit ? "gw_update" : "manage_template_submit")}
          </SoftButton>
        </DialogActions>
      </Dialog>

      <GoalWizardDrawer
        open={!!wizard}
        // Eski kalemde eser yok: ad ve biçim korunur, yalnız takip ayarları değişir.
        mode={editingItem && !editingItem.workId ? "edit" : "add"}
        initial={editingItem ? goalToInitial(editingItem) : undefined}
        title={t(editingItem ? "gw_title_edit" : "gw_title_add")}
        subtitle={t("manage_template_wizard_subtitle")}
        submitLabel={t(editingItem ? "gw_update" : "manage_template_add_to_list")}
        error={wizardError}
        onSubmit={handleWizardSubmit}
        onClose={() => setWizard(null)}
      />
    </>
  );
}
